import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Lightning, PaperPlaneTilt, Brain } from "@phosphor-icons/react";
import api, { extractError } from "@/lib/api";

const QUICK_PROMPTS = [
  "Design a push day for hypertrophy",
  "How do I fix my squat depth?",
  "Best cues for a heavy deadlift",
  "How many rest days per week?",
];

export default function Coach() {
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      role: "assistant",
      content:
        "I'm Spotter AI — your coach. Ask about programming, form cues, recovery, or nutrition. What are we working on?",
    },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const userMsgId = `u-${Date.now()}`;
    setMessages((m) => [...m, { id: userMsgId, role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: data.reply }]);
    } catch (err) {
      toast.error(extractError(err, "AI is having a moment. Try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 h-full flex flex-col" data-testid="coach-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#FF3B30] font-mono flex items-center gap-2">
          <Brain size={14} weight="bold" /> // Spotter AI
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase">Ask the coach</h1>
        <p className="text-zinc-500 mt-1 text-sm">Powered by Claude Sonnet 4.5.</p>
      </div>

      {/* Chat window */}
      <div className="border border-[#27272A] bg-[#121212] flex flex-col flex-1 min-h-[500px]">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] px-4 py-3 whitespace-pre-wrap text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-[#FF3B30] text-white"
                    : "bg-[#1E1E1E] border border-[#27272A] text-zinc-100"
                }`}
              >
                {m.role === "assistant" && (
                  <div className="text-[10px] uppercase tracking-widest text-[#FF3B30] font-mono mb-1 flex items-center gap-1">
                    <Lightning size={10} weight="bold" /> Spotter AI
                  </div>
                )}
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-[#1E1E1E] border border-[#27272A] px-4 py-3 text-zinc-500 text-sm">
                <span className="animate-pulse">Thinking…</span>
              </div>
            </div>
          )}
        </div>

        {/* Quick prompts */}
        {messages.length <= 1 && (
          <div className="p-4 border-t border-[#27272A] flex flex-wrap gap-2">
            {QUICK_PROMPTS.map((q) => (
              <button
                key={q}
                data-testid={`quick-prompt-${q.slice(0, 10).replace(/\s+/g, "-")}`}
                onClick={() => send(q)}
                className="text-xs border border-[#27272A] hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-400 px-3 py-1 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-[#27272A] p-3 flex items-center gap-2"
        >
          <input
            data-testid="coach-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about programming, form, recovery…"
            className="flex-1 bg-transparent outline-none text-white px-2 py-2 text-sm"
          />
          <button
            data-testid="coach-send-button"
            type="submit"
            disabled={loading || !input.trim()}
            className="btn-primary flex items-center gap-2 !py-2 !px-4"
          >
            <PaperPlaneTilt size={16} weight="bold" />
            <span className="hidden sm:inline">Send</span>
          </button>
        </form>
      </div>
    </div>
  );
}
